import React, { useState, useRef, useEffect } from 'react';
import { Hash, Smile, Paperclip, Clapperboard, SmilePlus, Trash2, Pencil, Reply, Send, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { createSocket } from '../../lib/ws';
import api from '../../lib/api';
import { avatar } from '../../mock';
import { toast } from 'sonner';
import { EmojiPicker, GifPicker, MessageMedia, PendingAttachments, uploadAttachments, ATTACH_ACCEPT } from './ChatKit';

const QUICK_EMOJIS = ['👍', '🔥', '❤️', '😂', '👀', '💯'];
const STAFF = ['admin', 'mod', 'moderator'];

const fmtDate = (ts) => {
  const d = new Date(ts || Date.now());
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : d.toLocaleDateString([], { day: 'numeric', month: 'numeric', year: 'numeric' });
};

// Community chat body, rendered inside the right-side drawer (ChatDrawer) and reusable elsewhere.
export function LiveChatPanel({ onOnlineCount }) {
  const { isAuthed, user, openAuth } = useAuth();
  const [rooms, setRooms] = useState([{ slug: 'general', name: 'Chat Global' }]);
  const [room, setRoom] = useState('general');
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [connected, setConnected] = useState(false);
  const [picker, setPicker] = useState(null);
  const [atts, setAtts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [reactFor, setReactFor] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [editing, setEditing] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const sockRef = useRef(null);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const isStaff = isAuthed && STAFF.includes(user?.role);

  useEffect(() => { api.get('/chat/rooms').then((r) => r.data?.length && setRooms(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const load = () => api.get('/presence/online').then((r) => onOnlineCount?.((r.data?.users || r.data || []).length)).catch(() => {});
    load();
    const iv = setInterval(load, 45000);
    return () => clearInterval(iv);
  }, [onOnlineCount]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => {
    let active = true;
    setMessages([]); setPicker(null); setReactFor(null); setConfirmDel(null); setEditing(null); setReplyTo(null);
    api.get(`/chat/${room}/history`).then((r) => { if (active) setMessages((r.data || []).slice(-60)); }).catch(() => {});
    if (!isAuthed) { setConnected(false); return () => { active = false; }; }
    const sock = createSocket(`/chat/${room}?token=${encodeURIComponent(localStorage.getItem('nuyed_token') || '')}`, {
      onOpen: () => setConnected(true),
      onMessage: (msg) => {
        if (msg?.type === 'message') setMessages((m) => [...m.slice(-120), msg.payload]);
        else if (msg?.type === 'message_updated') setMessages((m) => m.map((x) => (x.id === msg.payload.id ? msg.payload : x)));
        else if (msg?.type === 'message_deleted') setMessages((m) => m.filter((x) => x.id !== msg.payload.id));
        else if (msg?.type === 'error') toast.error(msg.detail || 'Message rejected');
      },
    });
    sockRef.current = sock;
    return () => { active = false; setConnected(false); sock.close(); };
  }, [room, isAuthed]);

  const send = () => {
    if (!isAuthed) return openAuth('login');
    if (!text.trim() && !atts.length) return;
    const body = replyTo ? `@${replyTo.username} ${text.trim()}` : text.trim();
    sockRef.current?.send({ text: body, attachments: atts });
    setText(''); setAtts([]); setPicker(null); setReplyTo(null);
  };
  const sendGif = (g) => { sockRef.current?.send({ text: text.trim(), gif_url: g.url }); setText(''); setPicker(null); };
  const pickFiles = async (e) => {
    setUploading(true);
    const ups = await uploadAttachments(e.target.files, 2 - atts.length);
    setAtts((a) => [...a, ...ups].slice(0, 2));
    setUploading(false);
    e.target.value = '';
  };
  const react = async (m, emoji) => { setReactFor(null); try { await api.post(`/chat/messages/${m.id}/react`, { emoji }); } catch {} };
  const remove = async (m) => { setConfirmDel(null); try { await api.delete(`/chat/messages/${m.id}`); } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); } };
  const saveEdit = async () => {
    if (!editing?.text.trim()) return;
    try { await api.put(`/chat/messages/${editing.id}`, { text: editing.text }); setEditing(null); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Edit failed'); }
  };
  const canModify = (m) => isAuthed && (m.username === user?.username || isStaff);
  const roomName = rooms.find((r) => r.slug === room)?.name || room;

  return (
    <div className="gx-chat-body" data-testid="floating-chat-panel">
      <div className="gx-chat-roombar">
        <label className="gx-chat-room-select">
          <select value={room} onChange={(e) => setRoom(e.target.value)} data-testid="floating-chat-room-select">
            {rooms.map((r) => <option key={r.slug} value={r.slug}>{r.name}</option>)}
          </select>
          <ChevronDown className="w-4 h-4" />
        </label>
        <span className={`gx-chat-conn ${connected ? 'is-on' : ''}`}>{connected ? 'Live' : isAuthed ? 'Connecting…' : 'Read only'}</span>
      </div>

      <div className="gx-chat-scroll">
        {messages.length === 0 && (
          <p className="text-center text-xs text-gx-muted py-14 flex items-center justify-center gap-1.5"><Hash className="w-3.5 h-3.5" /> No messages in {roomName} yet — say hi</p>
        )}
        {messages.map((m, i) => (
          <div key={m.id || i} className="gx-chat-msg group" data-testid={`floating-chat-msg-${m.id}`}>
            <img src={m.avatar_url || avatar(m.username)} className="gx-chat-avatar" alt="" />
            <div className="min-w-0 flex-1">
              <p className="gx-chat-meta">
                <span className="gx-chat-user">{m.username}</span>
                {STAFF.includes(m.role) && <span className="gx-chat-role">{m.role === 'admin' ? 'Admin' : 'Mod'}</span>}
                <span className="gx-chat-time">{fmtDate(m.ts)}</span>
                {m.edited_at && <span className="gx-chat-time">· edited</span>}
              </p>
              {editing?.id === m.id ? (
                <div className="flex items-center gap-2 mt-1">
                  <input value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditing(null); }} autoFocus className="gx-chat-edit" data-testid="floating-chat-edit-input" />
                  <button onClick={saveEdit} className="text-[11px] text-emerald-400 font-display font-semibold" data-testid="floating-chat-edit-save">Save</button>
                  <button onClick={() => setEditing(null)} className="text-[11px] text-gx-muted">Cancel</button>
                </div>
              ) : (
                <>
                  {m.text && <p className="gx-chat-text">{m.text}</p>}
                  <MessageMedia m={m} />
                </>
              )}
              {m.reactions && Object.keys(m.reactions).filter((k) => (m.reactions[k] || []).length).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {Object.entries(m.reactions).filter(([, v]) => (v || []).length).map(([emoji, uids]) => (
                    <button key={emoji} onClick={() => react(m, emoji)} className={`gx-chat-reaction ${isAuthed && uids.includes(user?.id) ? 'is-mine' : ''}`}>{emoji} <span>{uids.length}</span></button>
                  ))}
                </div>
              )}
            </div>
            {isAuthed && (
              <div className="gx-chat-actions">
                <button onClick={() => { setReplyTo(m); inputRef.current?.focus(); }} title="Reply" data-testid={`floating-reply-btn-${m.id}`}><Reply className="w-3.5 h-3.5" /></button>
                <button onClick={() => setReactFor(reactFor === m.id ? null : m.id)} title="React" data-testid={`floating-react-btn-${m.id}`}><SmilePlus className="w-3.5 h-3.5" /></button>
                {canModify(m) && m.text && <button onClick={() => setEditing({ id: m.id, text: m.text })} title="Edit" data-testid={`floating-edit-btn-${m.id}`}><Pencil className="w-3.5 h-3.5" /></button>}
                {canModify(m) && (confirmDel === m.id
                  ? <button onClick={() => remove(m)} className="is-danger text-[10px] font-display font-bold uppercase px-1" data-testid={`floating-del-confirm-${m.id}`}>Sure?</button>
                  : <button onClick={() => setConfirmDel(m.id)} className="is-danger" title="Delete" data-testid={`floating-del-btn-${m.id}`}><Trash2 className="w-3.5 h-3.5" /></button>)}
              </div>
            )}
            {reactFor === m.id && (
              <div className="gx-chat-quick">{QUICK_EMOJIS.map((e) => <button key={e} onClick={() => react(m, e)}>{e}</button>)}</div>
            )}
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <div className="gx-chat-composer">
        {picker === 'emoji' && <EmojiPicker className="absolute bottom-full left-3 mb-2 z-30" onPick={(e) => setText((t) => t + e)} />}
        {picker === 'gif' && <GifPicker className="absolute bottom-full left-3 mb-2 z-30" onPick={sendGif} onClose={() => setPicker(null)} />}
        {isAuthed ? (
          <>
            {replyTo && (
              <div className="gx-chat-replyto" data-testid="floating-chat-replyto">
                <Reply className="w-3 h-3" /> Replying to <b>@{replyTo.username}</b>
                <button onClick={() => setReplyTo(null)} className="ml-auto text-gx-muted hover:text-white">✕</button>
              </div>
            )}
            <PendingAttachments atts={atts} onRemove={(i) => setAtts((a) => a.filter((_, j) => j !== i))} />
            <div className="gx-chat-inputrow">
              <input ref={inputRef} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && send()} placeholder="Message… (Enter)" className="gx-chat-input" data-testid="floating-chat-input" />
              <div className="gx-chat-tools">
                <button onClick={() => setPicker(picker === 'emoji' ? null : 'emoji')} className={picker === 'emoji' ? 'is-active' : ''} title="Emoji" data-testid="floating-chat-emoji-btn"><Smile className="w-4 h-4" /></button>
                <button onClick={() => setPicker(picker === 'gif' ? null : 'gif')} className={`gx-chat-gif ${picker === 'gif' ? 'is-active' : ''}`} title="GIF" data-testid="floating-chat-gif-btn">GIF</button>
                <label className={uploading ? 'opacity-50 pointer-events-none' : ''} title="Attach image or file" data-testid="floating-chat-attach-btn">
                  <Paperclip className="w-4 h-4" />
                  <input type="file" multiple accept={ATTACH_ACCEPT} className="hidden" onChange={pickFiles} data-testid="floating-chat-attach-input" />
                </label>
              </div>
              <button onClick={send} className="gx-chat-send" aria-label="Send" data-testid="floating-chat-send"><Send className="w-4 h-4" /></button>
            </div>
          </>
        ) : (
          <button onClick={() => openAuth('login')} className="gx-btn-primary w-full text-xs justify-center" data-testid="floating-chat-login">Log in to chat</button>
        )}
      </div>
    </div>
  );
}
